import { useEffect, useState, useRef, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
    MagnifyingGlass, Plus, UploadSimple, DownloadSimple, Trash,
    X, PencilSimple, Archive, MapPin,
} from "@phosphor-icons/react";
import { format } from "date-fns";

const CONTACT_STATUS_STYLES = {
    active: "bg-emerald-500/15 text-emerald-300",
    unsubscribed: "bg-red-500/15 text-red-300",
};
const CONTACT_STATUS_DEFAULT = "bg-slate-700 text-slate-300";
const contactStatusClass = (s) => CONTACT_STATUS_STYLES[s] || CONTACT_STATUS_DEFAULT;

const EMPTY = { name: "", email: "", phone: "", company: "", state: "", county: "", tags: [], status: "active", notes: "" };

export default function AdminContacts() {
    const [contacts, setContacts] = useState([]);
    const [total, setTotal] = useState(0);
    const [byState, setByState] = useState({});
    const [stateFilter, setStateFilter] = useState("");
    const [sort, setSort] = useState("state");  // "state" or "recent"
    const [q, setQ] = useState("");
    const [loading, setLoading] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(EMPTY);
    const [exportingZip, setExportingZip] = useState(false);
    const fileRef = useRef(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = { q, sort, limit: 2000 };
            if (stateFilter) params.state = stateFilter;
            const { data } = await api.get("/admin/contacts", { params });
            setContacts(data.items);
            setTotal(data.total);
            setByState(data.by_state || {});
        } catch {
            toast.error("Failed to load contacts");
        } finally {
            setLoading(false);
        }
    }, [q, sort, stateFilter]);

    useEffect(() => {
        load();
        document.title = "Surveys2CAD | Contacts";
    }, [load]);

    const startNew = () => { setForm(EMPTY); setEditing(null); setShowForm(true); };
    const startEdit = (c) => {
        setForm({ ...c, tags: c.tags || [] });
        setEditing(c.id);
        setShowForm(true);
    };

    const save = async (e) => {
        e.preventDefault();
        try {
            const payload = { ...form, tags: typeof form.tags === "string" ? form.tags.split(",").map((t) => t.trim()).filter(Boolean) : form.tags };
            if (editing) {
                await api.put(`/admin/contacts/${editing}`, payload);
                toast.success("Contact updated");
            } else {
                await api.post("/admin/contacts", payload);
                toast.success("Contact added");
            }
            setShowForm(false);
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Save failed");
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete this contact?")) return;
        await api.delete(`/admin/contacts/${id}`);
        toast.success("Deleted");
        load();
    };

    const onImport = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const form = new FormData();
        form.append("file", file);
        try {
            const { data } = await api.post("/admin/contacts/import", form, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success(`Imported ${data.inserted} · skipped ${data.skipped}`);
            load();
        } catch {
            toast.error("Import failed");
        } finally {
            e.target.value = "";
        }
    };

    const onExport = async () => {
        try {
            const res = await api.get("/admin/contacts/export", { responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = "surveys2cad-contacts.csv";
            a.click();
            URL.revokeObjectURL(url);
        } catch {
            toast.error("Export failed");
        }
    };

    const onExportByState = async () => {
        setExportingZip(true);
        try {
            const res = await api.get("/admin/contacts/export-by-state", { responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = `surveys2cad-contacts-by-state-${new Date().toISOString().slice(0, 10)}.zip`;
            a.click();
            URL.revokeObjectURL(url);
            toast.success("Downloaded ZIP · one CSV per state (with counties)");
        } catch {
            toast.error("Export failed");
        } finally { setExportingZip(false); }
    };

    return (
        <div className="p-4 sm:p-6 lg:p-8" data-testid="admin-contacts">
            <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
                <div className="min-w-0">
                    <div className="overline mb-2">Contacts</div>
                    <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-50 tracking-tight">
                        Contact Lists
                    </h1>
                    <p className="text-slate-400 mt-2 text-sm">{total} contacts · filter, import CSV, export, and tag.</p>
                </div>
                <div className="flex flex-wrap gap-2">
                    <input ref={fileRef} type="file" accept=".csv" hidden onChange={onImport} data-testid="contacts-import-input" />
                    <button onClick={() => fileRef.current?.click()} className="btn-ghost" data-testid="contacts-import-btn">
                        <UploadSimple size={16} /> Import CSV
                    </button>
                    <button onClick={onExport} className="btn-ghost" data-testid="contacts-export-btn">
                        <DownloadSimple size={16} /> Export
                    </button>
                    <button
                        onClick={onExportByState}
                        disabled={exportingZip}
                        className="btn-ghost disabled:opacity-50"
                        data-testid="contacts-export-by-state-btn"
                        title="ZIP with one CSV per state (includes counties)"
                    >
                        <Archive size={16} /> {exportingZip ? "Zipping…" : "By State"}
                    </button>
                    <button onClick={startNew} className="btn-primary" data-testid="contacts-add-btn">
                        <Plus size={16} weight="bold" /> Add Contact
                    </button>
                </div>
            </div>

            <div className="flex items-center gap-3 mb-4 flex-wrap">
                <div className="relative flex-1 max-w-md min-w-[220px]">
                    <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                    <input
                        type="text" value={q} onChange={(e) => setQ(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && load()}
                        placeholder="Search name, email, company…"
                        className="w-full pl-10 bg-slate-900/70 border border-slate-700 rounded-lg px-3 py-2 text-slate-100 focus:outline-none focus:border-cyan-500"
                        data-testid="contacts-search"
                    />
                </div>
                <button onClick={load} className="btn-ghost !py-2 text-xs">Search</button>
                <div className="inline-flex rounded-lg border border-slate-800 overflow-hidden" data-testid="contacts-sort-toggle">
                    <button
                        onClick={() => setSort("state")}
                        className={`px-3 py-1.5 text-xs ${sort === "state" ? "bg-cyan-500/20 text-cyan-100" : "text-slate-400 hover:text-slate-200"}`}
                        data-testid="contacts-sort-state"
                    >
                        Sort by state
                    </button>
                    <button
                        onClick={() => setSort("recent")}
                        className={`px-3 py-1.5 text-xs ${sort === "recent" ? "bg-cyan-500/20 text-cyan-100" : "text-slate-400 hover:text-slate-200"}`}
                        data-testid="contacts-sort-recent"
                    >
                        Most recent
                    </button>
                </div>
            </div>

            {Object.keys(byState).length > 1 && (
                <div className="flex flex-wrap gap-1.5 mb-4" data-testid="contacts-state-chips">
                    <button
                        onClick={() => setStateFilter("")}
                        className={`px-2.5 py-1 rounded-full text-[11px] font-mono-alt border transition-colors ${
                            stateFilter === ""
                                ? "bg-cyan-500/20 border-cyan-400/60 text-cyan-100"
                                : "bg-slate-900 border-slate-800 text-slate-400 hover:text-slate-200"
                        }`}
                    >
                        All · {total}
                    </button>
                    {Object.entries(byState)
                        .sort((a, b) => b[1] - a[1])
                        .map(([st, n]) => (
                            <button
                                key={st}
                                onClick={() => setStateFilter(st === stateFilter ? "" : st)}
                                className={`px-2.5 py-1 rounded-full text-[11px] font-mono-alt border transition-colors ${
                                    stateFilter === st
                                        ? "bg-orange-500/20 border-orange-400/60 text-orange-100"
                                        : "bg-slate-900 border-slate-800 text-slate-400 hover:text-slate-200"
                                }`}
                                data-testid={`contacts-state-chip-${st}`}
                            >
                                {st || "Unknown"} · {n}
                            </button>
                        ))}
                </div>
            )}

            <div className="tech-card overflow-hidden">
                {(() => {
                    if (loading) {
                        return <div className="p-10 text-slate-400 text-center">Loading…</div>;
                    }
                    if (contacts.length === 0) {
                        return (
                            <div className="p-10 text-center">
                                <p className="text-slate-400 mb-4">No contacts yet.</p>
                                <button onClick={startNew} className="btn-primary inline-flex">
                                    <Plus size={14} weight="bold" /> Add your first contact
                                </button>
                            </div>
                        );
                    }
                    return (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-slate-800 bg-slate-900/40">
                                    {["Name", "Email", "Company", "State", "County", "Tags", "Status", "Added", ""].map((h) => (
                                        <th key={h} className="text-left px-4 py-3 overline">{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {contacts.map((c) => (
                                    <tr key={c.id} className="border-b border-slate-800/60 hover:bg-slate-900/30" data-testid={`contact-${c.id}`}>
                                        <td className="px-4 py-3 text-slate-100 font-heading font-medium">{c.name}</td>
                                        <td className="px-4 py-3 text-cyan-300">{c.email}</td>
                                        <td className="px-4 py-3 text-slate-300">{c.company || "—"}</td>
                                        <td className="px-4 py-3 text-slate-300">{c.state || "—"}</td>
                                        <td className="px-4 py-3 text-slate-300">
                                            {c.county ? (
                                                <span className="inline-flex items-center gap-1 text-[11px] text-orange-300 font-mono-alt">
                                                    <MapPin size={10} weight="fill" /> {c.county}
                                                </span>
                                            ) : "—"}
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="flex flex-wrap gap-1">
                                                {(c.tags || []).map((t) => (
                                                    <span key={t} className="px-2 py-0.5 text-[10px] rounded-full bg-cyan-500/10 text-cyan-300 border border-cyan-500/30 font-mono-alt">{t}</span>
                                                ))}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3">
                                            <span className={`px-2 py-0.5 rounded-full text-xs font-mono-alt ${contactStatusClass(c.status)}`}>{c.status}</span>
                                        </td>
                                        <td className="px-4 py-3 text-slate-400 font-mono-alt text-xs">
                                            {c.created_at ? format(new Date(c.created_at), "MMM d") : "—"}
                                        </td>
                                        <td className="px-4 py-3 text-right">
                                            <div className="flex justify-end gap-1">
                                                <button onClick={() => startEdit(c)} className="p-1.5 hover:text-cyan-300 text-slate-400" data-testid={`edit-contact-${c.id}`}>
                                                    <PencilSimple size={15} />
                                                </button>
                                                <button onClick={() => remove(c.id)} className="p-1.5 hover:text-red-400 text-slate-400" data-testid={`delete-contact-${c.id}`}>
                                                    <Trash size={15} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    );
                })()}
            </div>

            {/* Edit modal */}
            {showForm && (
                <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="contact-modal">
                    <form onSubmit={save} className="tech-card p-6 w-full max-w-xl max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between mb-6">
                            <h3 className="font-heading text-xl font-semibold text-slate-50">{editing ? "Edit" : "New"} contact</h3>
                            <button type="button" onClick={() => setShowForm(false)} className="text-slate-400 hover:text-slate-100">
                                <X size={20} />
                            </button>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <TextField label="Full name" required value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="cf-name" />
                            <TextField label="Email" type="email" required value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="cf-email" />
                            <TextField label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="cf-phone" />
                            <TextField label="Company" value={form.company} onChange={(v) => setForm({ ...form, company: v })} testid="cf-company" />
                            <TextField label="State" value={form.state} onChange={(v) => setForm({ ...form, state: v })} testid="cf-state" />
                            <TextField label="County" value={form.county || ""} onChange={(v) => setForm({ ...form, county: v })} testid="cf-county" />
                            <div>
                                <label className="overline mb-2 block">Status</label>
                                <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="w-full bg-slate-900/70 border border-slate-700 rounded-lg px-3 py-2.5 text-slate-100 focus:outline-none focus:border-cyan-500" data-testid="cf-status">
                                    <option value="active">active</option>
                                    <option value="inactive">inactive</option>
                                    <option value="unsubscribed">unsubscribed</option>
                                </select>
                            </div>
                            <div className="md:col-span-2">
                                <label className="overline mb-2 block">Tags (comma-separated)</label>
                                <input
                                    type="text"
                                    value={Array.isArray(form.tags) ? form.tags.join(", ") : form.tags}
                                    onChange={(e) => setForm({ ...form, tags: e.target.value })}
                                    placeholder="lead, boundary-survey, hot-lead"
                                    className="w-full bg-slate-900/70 border border-slate-700 rounded-lg px-3 py-2.5 text-slate-100 focus:outline-none focus:border-cyan-500"
                                    data-testid="cf-tags"
                                />
                            </div>
                            <div className="md:col-span-2">
                                <label className="overline mb-2 block">Notes</label>
                                <textarea
                                    rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                                    className="w-full bg-slate-900/70 border border-slate-700 rounded-lg px-3 py-2.5 text-slate-100 focus:outline-none focus:border-cyan-500 resize-none"
                                />
                            </div>
                        </div>
                        <div className="flex gap-2 justify-end mt-6">
                            <button type="button" onClick={() => setShowForm(false)} className="btn-ghost">Cancel</button>
                            <button type="submit" className="btn-primary" data-testid="cf-save">{editing ? "Save" : "Create"}</button>
                        </div>
                    </form>
                </div>
            )}
        </div>
    );
}

function TextField({ label, type = "text", required, value, onChange, testid, placeholder }) {
    return (
        <div>
            <label className="overline mb-2 block">{label}{required && <span className="text-orange-400 ml-1">*</span>}</label>
            <input
                type={type} required={required} value={value || ""} onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                className="w-full bg-slate-900/70 border border-slate-700 rounded-lg px-3 py-2.5 text-slate-100 focus:outline-none focus:border-cyan-500"
                data-testid={testid}
            />
        </div>
    );
}
